import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Music,
  User,
  Hash,
  Search,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  Info,
  Zap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { useFeedback } from "../contexts/FeedbackContext";
import { useQuota } from "../contexts/QuotaContext";

const API = process.env.REACT_APP_BACKEND_URL;

const IMPORT_TYPES = [
  {
    id: "performer_artist",
    label: "Performer / Artist",
    icon: Music,
    description: "Import tracks by a performer or artist from Spotify",
    available: true,
  },
  {
    id: "writer_composer",
    label: "Writer / Composer",
    icon: User,
    description: "Import works by a writer or composer name",
    available: false,
    badge: "Coming Soon",
  },
  {
    id: "ipi_number",
    label: "IPI Number",
    icon: Hash,
    description: "Import works associated with an IPI number",
    available: false,
    badge: "Coming Soon",
  },
];

const STATUS_CONFIG = {
  discovered: { label: "Discovered", color: "bg-blue-500/20 text-blue-400", icon: Search },
  selected: { label: "Selected", color: "bg-purple-500/20 text-purple-400", icon: CheckCircle2 },
  already_imported: { label: "Already Imported", color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
  queued: { label: "Queued", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
  processing: { label: "Processing", color: "bg-orange-500/20 text-orange-400", icon: Loader2 },
  completed: { label: "Completed", color: "bg-green-500/20 text-green-400", icon: CheckCircle2 },
  needs_review: { label: "Needs Review", color: "bg-amber-500/20 text-amber-400", icon: AlertCircle },
  failed: { label: "Failed", color: "bg-red-500/20 text-red-400", icon: XCircle },
  skipped: { label: "Skipped", color: "bg-gray-500/20 text-gray-400", icon: XCircle },
};

// Pipeline stage configuration for display - using generic labels to hide data sources
const STAGE_CONFIG = {
  queued: { label: "Queued", color: "text-gray-400", bgColor: "bg-gray-500/20" },
  resolving_spotify_metadata: { label: "Recording", color: "text-green-400", bgColor: "bg-green-500/20" },
  resolving_discogs_credits: { label: "Credits", color: "text-blue-400", bgColor: "bg-blue-500/20" },
  resolving_musicbrainz_credits: { label: "Credits", color: "text-purple-400", bgColor: "bg-purple-500/20" },
  resolving_bmi_publishing: { label: "Publishing", color: "text-orange-400", bgColor: "bg-orange-500/20" },
  populating_splits: { label: "Splits", color: "text-cyan-400", bgColor: "bg-cyan-500/20" },
  saving_record: { label: "Saving", color: "text-indigo-400", bgColor: "bg-indigo-500/20" },
  completed: { label: "Done", color: "text-green-400", bgColor: "bg-green-500/20" },
  needs_review: { label: "Review", color: "text-amber-400", bgColor: "bg-amber-500/20" },
  failed: { label: "Failed", color: "text-red-400", bgColor: "bg-red-500/20" },
};

// Pipeline stages in order for progress visualization
const PIPELINE_STAGES_ORDER = [
  "resolving_spotify_metadata",
  "resolving_musicbrainz_credits",
  "resolving_bmi_publishing",
  "populating_splits",
  "saving_record",
];

export default function ImportCatalogue() {
  const [importType, setImportType] = useState("performer_artist");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Performer/Artist fields
  const [artistName, setArtistName] = useState("");
  const [artistSpotifyUrl, setArtistSpotifyUrl] = useState("");
  const [maxTracks, setMaxTracks] = useState(50);
  const [includeFeatured, setIncludeFeatured] = useState(true);
  
  // Writer/Composer fields (scaffold)
  const [writerName, setWriterName] = useState("");
  const [writerPro, setWriterPro] = useState("");
  
  // IPI fields (scaffold)
  const [ipiNumber, setIpiNumber] = useState("");
  const [ipiName, setIpiName] = useState("");
  
  // Feedback context
  const { triggerFeedbackCheck } = useFeedback();
  const { unlimited, remaining, used, limit: quotaLimit, canUse, fetchQuota } = useQuota();
  const catalogueExhausted = !unlimited && !canUse("catalogue_import");
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [jobId, setJobId] = useState(null);
  
  // Import progress state
  const [importStatus, setImportStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);
  
  const handlePreview = async () => {
    setLoading(true);
    setPreviewData(null);
    setSelectedItems(new Set());
    setImportStatus(null);
    
    try {
      const payload = {
        import_type: importType,
        artist_name: artistName || null,
        artist_spotify_url: artistSpotifyUrl || null,
        max_tracks: maxTracks,
        include_featured: includeFeatured,
        writer_name: writerName || null,
        writer_pro: writerPro || null,
        ipi_number: ipiNumber || null,
        ipi_name: ipiName || null,
      };
      
      const response = await axios.post(`${API}/api/catalogue/import/preview`, payload);
      setPreviewData(response.data);
      setJobId(response.data.job_id);
      
      // Auto-select only items that are NOT already imported
      if (response.data.items && response.data.items.length > 0) {
        const selectableItems = response.data.items.filter(
          item => item.status === "discovered" || item.status === "selected"
        );
        const alreadyImportedCount = response.data.items.filter(
          item => item.status === "already_imported"
        ).length;
        
        setSelectedItems(new Set(selectableItems.map(item => item.id)));
        
        if (alreadyImportedCount > 0) {
          toast.success(`Discovered ${response.data.items.length} tracks (${alreadyImportedCount} already imported)`);
        } else {
          toast.success(`Discovered ${response.data.items.length} tracks`);
        }
      } else if (response.data.scaffold_message) {
        toast.info(response.data.scaffold_message);
      }
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response?.data?.detail || "Failed to preview catalogue");
    } finally {
      setLoading(false);
    }
  };
  
  const handleImport = async () => {
    if (!jobId || selectedItems.size === 0) {
      toast.error("Please select items to import");
      return;
    }
    
    setImporting(true);
    
    try {
      const response = await axios.post(`${API}/api/catalogue/import/start`, {
        job_id: jobId,
        selected_item_ids: Array.from(selectedItems),
      });
      
      toast.success(response.data.message);
      
      // Start polling for status with dynamic interval
      let pollCount = 0;
      const interval = setInterval(async () => {
        try {
          pollCount++;
          const statusResponse = await axios.get(`${API}/api/catalogue/import/status/${jobId}`);
          setImportStatus(statusResponse.data);
          
          // Update preview data with new item statuses
          if (statusResponse.data.items) {
            setPreviewData(prev => ({
              ...prev,
              items: statusResponse.data.items,
            }));
          }
          
          // Stop polling when all items are in terminal state or job is done
          const allTerminal = statusResponse.data.summary?.all_terminal;
          const jobDone = statusResponse.data.job?.status === "completed" || 
                         statusResponse.data.job?.status === "failed";
          
          if (allTerminal || jobDone) {
            clearInterval(interval);
            setPollingInterval(null);
            setImporting(false);
            
            const summary = statusResponse.data.summary;
            if (summary) {
              const msg = `Import finished: ${summary.status_counts?.completed || 0} completed, ${summary.status_counts?.needs_review || 0} need review, ${summary.status_counts?.failed || 0} failed`;
              if (summary.status_counts?.failed > 0) {
                toast.warning(msg);
              } else {
                toast.success(msg);
              }
              
              // Trigger feedback check after catalogue import completes
              if ((summary.status_counts?.completed || 0) > 0) {
                triggerFeedbackCheck("catalogue_import");
                fetchQuota();
              }
            }
          }
        } catch (err) {
          console.error("Status polling error:", err);
        }
      }, 2000);  // Poll every 2 seconds
      
      setPollingInterval(interval);
      
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to start import");
      setImporting(false);
    }
  };
  
  const toggleSelectAll = () => {
    if (!previewData?.items) return;
    
    // Only toggle selectable items (not already_imported)
    const selectableItems = previewData.items.filter(
      item => item.status === "discovered" || item.status === "selected"
    );
    
    if (selectedItems.size === selectableItems.length && selectableItems.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(selectableItems.map(item => item.id)));
    }
  };
  
  const toggleSelectItem = (itemId) => {
    // Prevent selecting already_imported items
    const item = previewData?.items?.find(i => i.id === itemId);
    if (item && item.status === "already_imported") return;
    
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };
  
  const formatDuration = (ms) => {
    if (!ms) return "";
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  
  const getSourceBadge = (source) => {
    const colors = {
      top_tracks: "bg-green-500/20 text-green-400",
      album: "bg-blue-500/20 text-blue-400",
      featured: "bg-purple-500/20 text-purple-400",
    };
    const labels = {
      top_tracks: "Top Track",
      album: "Album",
      featured: "Featured",
    };
    return (
      <Badge className={`${colors[source] || "bg-gray-500/20 text-gray-400"} text-xs`}>
        {labels[source] || source}
      </Badge>
    );
  };
  
  const renderImportTypeForm = () => {
    const selectedType = IMPORT_TYPES.find(t => t.id === importType);
    
    if (importType === "performer_artist") {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="artistName">Artist Name *</Label>
            <Input
              id="artistName"
              placeholder="e.g., Taylor Swift, The Beatles"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              data-testid="artist-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="artistSpotifyUrl">Spotify Artist URL (optional)</Label>
            <Input
              id="artistSpotifyUrl"
              placeholder="https://open.spotify.com/artist/..."
              value={artistSpotifyUrl}
              onChange={(e) => setArtistSpotifyUrl(e.target.value)}
              data-testid="artist-spotify-url-input"
            />
            <p className="text-xs text-syncchain-text-muted">
              Providing the Spotify URL ensures accurate artist matching
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxTracks">Max Tracks</Label>
              <Select value={maxTracks.toString()} onValueChange={(v) => setMaxTracks(parseInt(v))}>
                <SelectTrigger data-testid="max-tracks-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 tracks</SelectItem>
                  <SelectItem value="50">50 tracks</SelectItem>
                  <SelectItem value="100">100 tracks</SelectItem>
                  <SelectItem value="200">200 tracks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Include Featured</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  checked={includeFeatured}
                  onCheckedChange={setIncludeFeatured}
                  data-testid="include-featured-switch"
                />
                <span className="text-sm text-syncchain-text-secondary">
                  {includeFeatured ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (importType === "writer_composer") {
      return (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-400">Coming Soon</p>
                <p className="text-sm text-syncchain-text-secondary mt-1">
                  Writer/Composer import will search BMI Songview by writer name, 
                  extract work titles and ISWC, then resolve likely recordings via Spotify/MusicBrainz.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2 opacity-60">
            <Label htmlFor="writerName">Writer/Composer Name *</Label>
            <Input
              id="writerName"
              placeholder="e.g., Max Martin, Diane Warren"
              value={writerName}
              onChange={(e) => setWriterName(e.target.value)}
              disabled
            />
          </div>
          
          <div className="space-y-2 opacity-60">
            <Label htmlFor="writerPro">PRO (optional)</Label>
            <Select value={writerPro} onValueChange={setWriterPro} disabled>
              <SelectTrigger>
                <SelectValue placeholder="Select PRO..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BMI">BMI</SelectItem>
                <SelectItem value="ASCAP">ASCAP</SelectItem>
                <SelectItem value="SESAC">SESAC</SelectItem>
                <SelectItem value="PRS">PRS</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }
    
    if (importType === "ipi_number") {
      return (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-400">Coming Soon</p>
                <p className="text-sm text-syncchain-text-secondary mt-1">
                  IPI Number import will search BMI Songview by IPI, retrieve associated works, 
                  then resolve likely recordings via Spotify/MusicBrainz.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2 opacity-60">
            <Label htmlFor="ipiNumber">IPI Number *</Label>
            <Input
              id="ipiNumber"
              placeholder="e.g., 00073065011"
              value={ipiNumber}
              onChange={(e) => setIpiNumber(e.target.value)}
              disabled
            />
          </div>
          
          <div className="space-y-2 opacity-60">
            <Label htmlFor="ipiName">Name (optional)</Label>
            <Input
              id="ipiName"
              placeholder="Name to help confirm match"
              value={ipiName}
              onChange={(e) => setIpiName(e.target.value)}
              disabled
            />
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  const renderProgress = () => {
    if (!importStatus?.job) return null;
    
    const summary = importStatus.summary || {};
    const statusCounts = summary.status_counts || {};
    const stageCounts = summary.stage_counts || {};
    const progressPct = summary.progress_pct || 0;
    const totalItems = summary.total || 0;
    const currentlyProcessing = summary.currently_processing || 0;
    
    return (
      <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10" data-testid="import-progress-panel">
        {/* Overall Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Import Progress</span>
            <span className="text-sm text-syncchain-text-secondary">
              {Math.round(progressPct)}% complete
            </span>
          </div>
          <Progress value={progressPct} className="h-2" />
        </div>
        
        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="flex items-center gap-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
            <Clock className="w-4 h-4 text-yellow-400" />
            <div>
              <p className="text-xs text-syncchain-text-muted">Queued</p>
              <p className="text-sm font-medium text-yellow-400">{statusCounts.queued || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-orange-500/10 border border-orange-500/20">
            <Loader2 className="w-4 h-4 text-orange-400 animate-spin" />
            <div>
              <p className="text-xs text-syncchain-text-muted">Processing</p>
              <p className="text-sm font-medium text-orange-400">{currentlyProcessing}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-green-500/10 border border-green-500/20">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <div>
              <p className="text-xs text-syncchain-text-muted">Completed</p>
              <p className="text-sm font-medium text-green-400">{statusCounts.completed || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <div>
              <p className="text-xs text-syncchain-text-muted">Review</p>
              <p className="text-sm font-medium text-amber-400">{statusCounts.needs_review || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
            <XCircle className="w-4 h-4 text-red-400" />
            <div>
              <p className="text-xs text-syncchain-text-muted">Failed</p>
              <p className="text-sm font-medium text-red-400">{statusCounts.failed || 0}</p>
            </div>
          </div>
        </div>
        
        {/* Pipeline Stage Breakdown (only show when processing) */}
        {currentlyProcessing > 0 && Object.keys(stageCounts).length > 0 && (
          <div className="pt-2 border-t border-white/10">
            <p className="text-xs text-syncchain-text-muted mb-2">Current Pipeline Stages</p>
            <div className="flex flex-wrap gap-2">
              {PIPELINE_STAGES_ORDER.map(stage => {
                const count = stageCounts[stage] || 0;
                if (count === 0) return null;
                const stageConfig = STAGE_CONFIG[stage] || STAGE_CONFIG.queued;
                return (
                  <Badge 
                    key={stage} 
                    className={`${stageConfig.bgColor} ${stageConfig.color} text-xs`}
                  >
                    {stageConfig.label}: {count}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="import-catalogue-page">
      {/* Quota Banner */}
      {!unlimited && (
        <div
          className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
            catalogueExhausted
              ? "bg-red-500/10 border-red-500/30 text-red-300"
              : "bg-syncchain-accent/10 border-syncchain-accent/30 text-syncchain-text-secondary"
          }`}
          data-testid="catalogue-quota-banner"
        >
          <div className="flex items-center gap-2 text-sm">
            {catalogueExhausted ? <AlertCircle className="w-4 h-4 text-red-400" /> : <Music className="w-4 h-4" />}
            <span>
              {catalogueExhausted
                ? "Catalogue imports are not available on your current plan."
                : `${used("catalogue_import")} / ${quotaLimit("catalogue_import")} catalogue imports used`}
            </span>
          </div>
          {catalogueExhausted ? (
            <a href="/pricing" className="flex items-center gap-1 text-xs font-semibold text-syncchain-accent hover:text-syncchain-accent-hover transition-colors">
              <Zap className="w-3 h-3" /> Upgrade
            </a>
          ) : (
            <span className="text-xs font-mono">{remaining("catalogue_import")} remaining</span>
          )}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold text-syncchain-text">Import Catalogue</h1>
        <p className="text-syncchain-text-secondary mt-1">
          Discover and import songs based on performer, writer, or IPI number
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Import Type Selection */}
        <Card className="lg:col-span-1 bg-syncchain-paper border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Import Type</CardTitle>
            <CardDescription>Choose how you want to discover tracks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {IMPORT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => {
                  setImportType(type.id);
                  setPreviewData(null);
                  setSelectedItems(new Set());
                }}
                disabled={!type.available}
                className={`w-full p-4 rounded-lg border transition-all text-left ${
                  importType === type.id
                    ? "border-syncchain-accent bg-syncchain-accent/10"
                    : "border-white/10 hover:border-white/20 hover:bg-white/5"
                } ${!type.available ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`import-type-${type.id}`}
              >
                <div className="flex items-start gap-3">
                  <type.icon className={`w-5 h-5 mt-0.5 ${
                    importType === type.id ? "text-syncchain-accent" : "text-syncchain-text-secondary"
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        importType === type.id ? "text-syncchain-accent" : "text-syncchain-text"
                      }`}>
                        {type.label}
                      </span>
                      {type.badge && (
                        <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                          {type.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-syncchain-text-muted mt-1">
                      {type.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
        
        {/* Import Form */}
        <Card className="lg:col-span-2 bg-syncchain-paper border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">
              {IMPORT_TYPES.find(t => t.id === importType)?.label} Import
            </CardTitle>
            <CardDescription>
              {IMPORT_TYPES.find(t => t.id === importType)?.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderImportTypeForm()}
            
            <div className="flex gap-3">
              <Button
                onClick={handlePreview}
                disabled={loading || importing || catalogueExhausted || (importType === "performer_artist" && !artistName && !artistSpotifyUrl)}
                className="bg-syncchain-accent hover:bg-syncchain-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="preview-catalogue-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Preview Catalogue
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Preview Results */}
      {previewData && (
        <Card className="bg-syncchain-paper border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-3">
                  {previewData.artist_info && (
                    <>
                      {previewData.artist_info.image_url && (
                        <img
                          src={previewData.artist_info.image_url}
                          alt={previewData.artist_info.name}
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <span>{previewData.artist_info.name}</span>
                    </>
                  )}
                  {!previewData.artist_info && "Discovery Results"}
                </CardTitle>
                <CardDescription>
                  {previewData.total_discovered > 0 
                    ? `${previewData.total_discovered} tracks discovered • ${selectedItems.size} selected`
                    : previewData.scaffold_message || "No tracks discovered"
                  }
                </CardDescription>
              </div>
              
              {previewData.items && previewData.items.length > 0 && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="border-white/20"
                    data-testid="select-all-btn"
                  >
                    {selectedItems.size === previewData.items.length ? "Deselect All" : "Select All"}
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={importing || selectedItems.size === 0}
                    className="bg-syncchain-accent hover:bg-syncchain-accent/90"
                    data-testid="import-selected-btn"
                  >
                    {importing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Import {selectedItems.size} Selected
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          
          {importing && renderProgress()}
          
          {previewData.items && previewData.items.length > 0 && (
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedItems.size === previewData.items.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Stage</TableHead>
                      <TableHead className="text-center">Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.items.map((item) => {
                      const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.discovered;
                      const StatusIcon = statusConfig.icon;
                      const isAlreadyImported = item.status === "already_imported";
                      const isSelectable = (item.status === "discovered" || item.status === "selected") && !isAlreadyImported;
                      const isProcessing = item.status === "processing";
                      const isCompleted = item.status === "completed";
                      const isFailed = item.status === "failed";
                      const needsReview = item.status === "needs_review";
                      
                      // Get stage configuration
                      const currentStage = item.current_stage || "queued";
                      const stageConfig = STAGE_CONFIG[currentStage] || STAGE_CONFIG.queued;
                      
                      // Row styling based on status
                      const rowClass = isAlreadyImported
                        ? "border-white/10 bg-emerald-500/5 opacity-60"
                        : isProcessing 
                          ? "border-white/10 bg-orange-500/5 hover:bg-orange-500/10" 
                          : isCompleted 
                            ? "border-white/10 bg-green-500/5 hover:bg-green-500/10"
                            : isFailed
                              ? "border-white/10 bg-red-500/5 hover:bg-red-500/10"
                              : needsReview
                                ? "border-white/10 bg-amber-500/5 hover:bg-amber-500/10"
                                : "border-white/10 hover:bg-white/5";
                      
                      return (
                        <TableRow 
                          key={item.id} 
                          className={rowClass}
                          data-testid={`track-row-${item.id}`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.has(item.id)}
                              onCheckedChange={() => toggleSelectItem(item.id)}
                              disabled={!isSelectable}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {item.image_url && (
                                <img
                                  src={item.image_url}
                                  alt={item.discovered_title}
                                  className="w-10 h-10 rounded"
                                />
                              )}
                              <div>
                                <p className="font-medium text-syncchain-text truncate max-w-[200px]">
                                  {item.discovered_title}
                                </p>
                                <p className="text-xs text-syncchain-text-muted">
                                  {item.album} {item.release_date && `• ${item.release_date.split("-")[0]}`}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-syncchain-text-secondary truncate max-w-[150px]">
                            {item.discovered_artist}
                          </TableCell>
                          <TableCell>
                            {getSourceBadge(item.discovery_source)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={`${statusConfig.color} text-xs`}>
                              <StatusIcon className={`w-3 h-3 mr-1 ${isProcessing ? "animate-spin" : ""}`} />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {isProcessing && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge className={`${stageConfig.bgColor} ${stageConfig.color} text-xs`}>
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      {stageConfig.label}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">{item.progress_message || "Processing..."}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {isCompleted && (
                              <Badge className="bg-green-500/20 text-green-400 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Done
                              </Badge>
                            )}
                            {needsReview && (
                              <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Review
                              </Badge>
                            )}
                            {isFailed && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge className="bg-red-500/20 text-red-400 text-xs">
                                      <XCircle className="w-3 h-3 mr-1" />
                                      Failed
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs max-w-[200px]">{item.error_message || "Unknown error"}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {isAlreadyImported && (
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Exists
                              </Badge>
                            )}
                            {!isProcessing && !isCompleted && !isFailed && !needsReview && !isAlreadyImported && (
                              <span className="text-xs text-syncchain-text-muted">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.linked_search_id ? (
                              <a
                                href={`/search/${item.linked_search_id}`}
                                className="text-syncchain-accent hover:underline text-xs flex items-center justify-center gap-1"
                                data-testid={`view-result-${item.id}`}
                              >
                                <ExternalLink className="w-3 h-3" />
                                View
                              </a>
                            ) : (
                              <span className="text-xs text-syncchain-text-muted">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          )}
          
          {previewData.scaffold_message && (
            <CardContent>
              <div className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-syncchain-text-secondary">{previewData.scaffold_message}</p>
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
